/**
 * CollegeServiceIntegration - Event Emission Patches
 *
 * Purpose: Add event emission to existing college-journey-service.js methods.
 * This file provides wrapper functions that emit events when colleges change.
 *
 * Usage Pattern:
 *   Instead of:
 *     await collegeJourneyService.addCollege(data, userId);
 *
 *   Use:
 *     import { addCollegeWithEvents } from './services/events/CollegeServiceIntegration.js';
 *     await addCollegeWithEvents(data, userId);
 *
 * This allows gradual migration without breaking existing code.
 */

import { collegeJourneyService } from '../../lib/college-journey-service.js';
import CollegeEventBus from './CollegeEventBus.js';

/**
 * Add college with event emission
 */
export async function addCollegeWithEvents(collegeData, userId) {
  console.log('[CollegeServiceIntegration] 🎯 Adding college with events:', collegeData.name);

  try {
    // Call original service
    const result = await collegeJourneyService.addCollege(collegeData, userId);
    console.log('[CollegeServiceIntegration] ✅ College added, now emitting event...');

    // Emit event with detailed payload logging
    const eventPayload = {
      userId,
      collegeId: result.id,
      collegeName: result.name || collegeData.name,
      applicationRound: result.application_round || collegeData.application_round,
      category: result.category || collegeData.category,
      globalCollegeId: result.global_college_id || collegeData.global_college_id
    };

    console.log('[CollegeServiceIntegration] 📤 Emitting COLLEGE_ADDED event with payload:', {
      ...eventPayload,
      collegeNameSource: result.name ? 'result.name' : 'collegeData.name'
    });

    const eventResult = await CollegeEventBus.emit(
      CollegeEventBus.events.COLLEGE_ADDED,
      eventPayload,
      {
        priority: 'high',
        source: 'user'
      }
    );

    console.log('[CollegeServiceIntegration] 📡 Event emission result:', eventResult);

    return result;

  } catch (error) {
    console.error('[CollegeServiceIntegration] ❌ Failed to add college:', error);
    throw error;
  }
}

/**
 * Update college with event emission
 */
export async function updateCollegeWithEvents(collegeId, updates, userId) {
  console.log('[CollegeServiceIntegration] Updating college with events:', collegeId);

  try {
    // Get current state first
    const { data: currentCollege } = await supabase
      .from('college_journey_colleges')
      .select('*')
      .eq('id', collegeId)
      .single();

    // Call original service (you'll need to implement this method in college-journey-service.js)
    const result = await collegeJourneyService.updateCollege(collegeId, updates);

    // Emit appropriate events based on what changed
    if (updates.application_round && updates.application_round !== currentCollege.application_round) {
      await CollegeEventBus.emit(
        CollegeEventBus.events.APPLICATION_ROUND_CHANGED,
        {
          userId,
          collegeId,
          collegeName: currentCollege.name,
          oldRound: currentCollege.application_round,
          newRound: updates.application_round
        },
        {
          priority: 'critical',
          source: 'user'
        }
      );
    }

    if (updates.category && updates.category !== currentCollege.category) {
      await CollegeEventBus.emit(
        CollegeEventBus.events.CATEGORY_CHANGED,
        {
          userId,
          collegeId,
          collegeName: currentCollege.name,
          oldCategory: currentCollege.category,
          newCategory: updates.category
        },
        {
          priority: 'normal',
          source: 'user'
        }
      );
    }

    // Check deadline changes
    const deadlineFields = ['ed_deadline', 'ea_deadline', 'rd_deadline', 'ed2_deadline'];
    for (const field of deadlineFields) {
      if (updates[field] && updates[field] !== currentCollege[field]) {
        await CollegeEventBus.emit(
          CollegeEventBus.events.DEADLINE_CHANGED,
          {
            userId,
            collegeId,
            collegeName: currentCollege.name,
            deadlineType: field,
            oldDeadline: currentCollege[field],
            newDeadline: updates[field]
          },
          {
            priority: 'high',
            source: 'user'
          }
        );
      }
    }

    // General update event
    await CollegeEventBus.emit(
      CollegeEventBus.events.COLLEGE_UPDATED,
      {
        userId,
        collegeId,
        collegeName: currentCollege.name,
        updates: Object.keys(updates)
      },
      {
        priority: 'normal',
        source: 'user'
      }
    );

    return result;

  } catch (error) {
    console.error('[CollegeServiceIntegration] Failed to update college:', error);
    throw error;
  }
}

/**
 * Remove college with event emission
 */
export async function removeCollegeWithEvents(collegeId, userId) {
  console.log('[CollegeServiceIntegration] Removing college with events:', collegeId);

  try {
    // Get college data before deletion
    const { data: college } = await supabase
      .from('college_journey_colleges')
      .select('*')
      .eq('id', collegeId)
      .single();

    // Call original service (you'll need to implement this)
    const result = await collegeJourneyService.removeCollege(collegeId, userId);

    // Emit event
    await CollegeEventBus.emit(
      CollegeEventBus.events.COLLEGE_REMOVED,
      {
        userId,
        collegeId,
        collegeName: college?.name,
        applicationRound: college?.application_round
      },
      {
        priority: 'high',
        source: 'user'
      }
    );

    return result;

  } catch (error) {
    console.error('[CollegeServiceIntegration] Failed to remove college:', error);
    throw error;
  }
}

/**
 * Bulk update application rounds (for when student sets all at once)
 */
export async function bulkUpdateApplicationRounds(colleges, userId) {
  console.log('[CollegeServiceIntegration] Bulk updating application rounds for', colleges.length, 'colleges');

  const results = [];

  for (const college of colleges) {
    try {
      await updateCollegeWithEvents(
        college.id,
        { application_round: college.application_round },
        userId
      );
      results.push({ collegeId: college.id, success: true });
    } catch (error) {
      console.error(`[CollegeServiceIntegration] Failed to update ${college.id}:`, error);
      results.push({ collegeId: college.id, success: false, error: error.message });
    }
  }

  return results;
}

/**
 * Initialize event system on app start
 * Call this in your main App.jsx or index.jsx
 */
export async function initializeEventSystem() {
  console.log('[CollegeServiceIntegration] 🚀 Initializing event system...');

  try {
    // Import and register all handlers
    const { registerAllHandlers, setupPostgresNotificationListener } = await import('./EventHandlerRegistry');

    registerAllHandlers();
    setupPostgresNotificationListener();

    console.log('[CollegeServiceIntegration] ✅ Event system initialized');

    return {
      success: true,
      message: 'Event system is active'
    };

  } catch (error) {
    console.error('[CollegeServiceIntegration] ❌ Failed to initialize event system:', error);
    throw error;
  }
}
